import type { DevBundleOverrides } from "../runtime-config/read-shell-config";
import type { ManifestVersionOverride } from "./types";

export type DevToolsSnapshot = {
  shellVersion: string;
  defaultShellVersion: string;
  shellVersionOverride: string | null;
  availableShellVersions: Array<string>;
  bundleOverrides: DevBundleOverrides;
  manifestVersionOverride: ManifestVersionOverride | null;
};

export type DevToolsMutationResponse = DevToolsSnapshot & {
  reloadRequired: boolean;
  message: string;
};

export async function fetchDevToolsSnapshot(
  apiBaseUrl: string,
): Promise<DevToolsSnapshot> {
  const response = await fetch(`${apiBaseUrl}/dev-tools`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to load dev tools snapshot (${response.status})`);
  }
  return (await response.json()) as DevToolsSnapshot;
}

export async function putBundleOverride(
  apiBaseUrl: string,
  body: {
    kind: "view" | "module";
    id: string;
    bundleUrl: string;
    manifestUrl?: string;
  },
): Promise<DevToolsMutationResponse> {
  const response = await fetch(`${apiBaseUrl}/dev-tools/bundle-overrides`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Failed to save bundle override (${response.status})`);
  }
  return (await response.json()) as DevToolsMutationResponse;
}

export async function deleteBundleOverride(
  apiBaseUrl: string,
  kind: "view" | "module",
  id: string,
): Promise<DevToolsMutationResponse> {
  const response = await fetch(
    `${apiBaseUrl}/dev-tools/bundle-overrides/${kind}/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to remove bundle override (${response.status})`);
  }
  return (await response.json()) as DevToolsMutationResponse;
}

export async function clearBundleOverrides(
  apiBaseUrl: string,
): Promise<DevToolsMutationResponse> {
  const response = await fetch(`${apiBaseUrl}/dev-tools/bundle-overrides`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to clear bundle overrides (${response.status})`);
  }
  return (await response.json()) as DevToolsMutationResponse;
}

export async function putShellVersionOverride(
  apiBaseUrl: string,
  version: string | null,
): Promise<DevToolsMutationResponse> {
  const response = await fetch(`${apiBaseUrl}/dev-tools/shell-version`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ version }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update shell version override (${response.status})`);
  }
  return (await response.json()) as DevToolsMutationResponse;
}

export async function putManifestVersionOverride(
  apiBaseUrl: string,
  branchId: string,
  version: string | null,
): Promise<DevToolsMutationResponse> {
  const response = await fetch(`${apiBaseUrl}/dev-tools/manifest-version`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ branchId, version }),
  });
  if (!response.ok) {
    throw new Error(
      `Failed to update manifest version override (${response.status})`,
    );
  }
  return (await response.json()) as DevToolsMutationResponse;
}
